import React, { Dispatch } from 'react';

import { useMediaQuery } from 'react-responsive';
import classNames from 'classnames';
import moment from 'moment';

import { ReactComponent as IconChangePlanModal } from 'assets/images/change_plan_modal.svg';
import { ReactComponent as IconSuccessModal } from 'assets/images/success.svg';
import { IRegisterPlan, RequestStatusEnum } from 'common/types';
import {
  selectPendingUpdate,
  selectUpdateUserPlanStatus,
  setUpdateUserPlanStatus,
  selectUserPlan
} from 'modules/me/slice';
import ACTIONS from 'modules/rootActions';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import Modal from 'components/Modal';

interface Props {
  planSelected: IRegisterPlan | null;
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<boolean>;
  currentPlan: string | null | undefined;
  isButtonDisabled: boolean;
  setIsButtonDisabled: Dispatch<boolean>;
}

function ChangePlanModal({
  currentPlan,
  planSelected,
  isModalOpen,
  setIsModalOpen,
  isButtonDisabled,
  setIsButtonDisabled,
}: Props) {
  const dispatch = useAppDispatch();
  const isPending = useAppSelector(selectPendingUpdate);
  const updateUserPlanStatus = useAppSelector(selectUpdateUserPlanStatus);
  const userPlan = useAppSelector(selectUserPlan);

  const isMobile = useMediaQuery({
    query: '(max-width: 500px)',
  });

  const handleModalClose = () => {
    setIsModalOpen(false);
    dispatch(setUpdateUserPlanStatus(null));
  };

  const handleSelectPlan = (plan: number | undefined) => {
    setIsButtonDisabled(true);
    const payload = { planId: plan };
    dispatch({ type: ACTIONS.UPDATE_USER_PLAN, payload });
  };

  const renderUserPlanModalContent = () => {
    if (updateUserPlanStatus === RequestStatusEnum.SUCCESS) {
      return (
        <>
          <IconSuccessModal className="icon h-[85px] md:h-auto" />
          <h1 className="title form-control text-center text-[16px] font-bold md:text-3xl">
            Thank you!
          </h1>
          <p className="text-center text-[16px] md:text-base">
            You have successfully changed your plan from{' '}
            <span className="capitalize">{currentPlan}</span> to{' '}
            <span className="capitalize">
              {planSelected?.name}
            </span>
            .
          </p>

          <button
            className="button btn mx-auto flex h-[60px] w-full rounded-full border-none bg-flexi-green-1 text-[16px] capitalize"
            onClick={() => {
              setIsModalOpen(false);
              window.parent.location.href = '/app/account/plan-details';
            }}
          >
            ok
          </button>
        </>
      );
    }

    return (
      <>
        <IconChangePlanModal className="icon h-[85px] md:h-auto " />
        <h1 className="title form-control text-center text-[16px] font-bold md:text-3xl">
          Are you sure you want to change your plan?
        </h1>
        <p className="text-center text-[14px] md:text-base">
          Please be advised that your new plan will start on your next renewal date ({moment(userPlan?.subscription?.renewalAt).format('D MMMM YYYY')}). Until then, you’ll still have access to your current plan’s features and&nbsp;rates.
        </p>
        <button
          className={classNames(
            'btn btn-primary mx-auto mt-8 h-[50px] w-[calc(100vw-175px)] rounded-full capitalize disabled:text-white md:h-[60px] md:w-full',
            {
              loading: isPending,
            },
          )}
          onClick={() => handleSelectPlan(planSelected?.id)}
          disabled={isButtonDisabled || isPending}
        >
          Confirm
        </button>
      </>
    );
  };

  return (
    <Modal
      isModalOpen={isModalOpen}
      handleClose={() => {
        handleModalClose();
        if (updateUserPlanStatus === RequestStatusEnum.SUCCESS)
               window.parent.location.href = '/app/account/plan-details';
      }}
      px={isMobile ? 'px-[25px]' : 'px-[120px]'}
      mx={isMobile ? '30px' : '0px'}
    >
      {renderUserPlanModalContent()}
    </Modal>
  );
}

export default ChangePlanModal;
